import { useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { getPropertyDetail } from '@api'
import { selectedGroup, selectedProperty } from '@reducer/profile'
import { resolveBuildingCandidate } from '@/common/buildingCandidate'
import { IEntity } from '@/types'

/**
 * Gives the session a building to work with before the user picks one.
 *
 * Without this the launcher opens with nothing selected after every login
 * (and after entering impersonation), which disables every building-scoped tile
 * and sends every scoped sidebar link Home. The building is resolved in order of
 * how strongly it reflects intent:
 *
 *   1. the URL — /b/:bId already owns the selection, BuildingScope syncs it
 *   2. whatever survived in the persisted profile
 *   3. the token's own candidates (see resolveBuildingCandidate)
 *
 * Deliberately silent past that point: a user with several buildings and no
 * recorded default is asked rather than guessed at, because silently scoping an
 * admin to an arbitrary building is worse than an empty picker.
 *
 * Mounted once in the authenticated layout so login, reload and impersonation
 * all go through the same path instead of three call sites that drift.
 */
export const useBuildingBootstrap = () => {
  const dispatch = useDispatch()
  const { pathname } = useLocation()
  const { user, selectedPropertyId, selectedGroupId } = useSelector((state: any) => state.profile)
  const resolving = useRef('')

  useEffect(() => {
    if (pathname.startsWith('/b/')) return
    if (selectedPropertyId) {
      // A persisted profile can carry a building with an empty group (legacy
      // clearGroup blobs predating the paired clear in Logo); group-scoped
      // payloads built from that state silently no-op server-side, so repair
      // the group from the selected building's own record.
      if (!user || selectedGroupId) return
      const attempt = `repair:${selectedPropertyId}`
      if (resolving.current === attempt) return
      resolving.current = attempt
      getPropertyDetail<IEntity>({ eId: selectedPropertyId })
        .then((detail) => {
          if (!detail?.GroupId || detail.Status === 'Archived') return
          dispatch(selectedGroup({ groupId: detail.GroupId, groupName: '', group: {} }))
        })
        .catch(() => {
          /* checkAuth session recovery remains as the fallback repair */
        })
      return
    }
    // A group is no longer required up front: sessions can land here with an
    // empty selectedGroupId (impersonation, cross-group browsing), and the
    // candidate below is resolved from the token regardless of group.
    if (!user) return
    const candidate = resolveBuildingCandidate(user)
    // One attempt per candidate per group: a building that fails validation
    // must not be retried on every render, and clearing the selection by hand
    // (the management-tag filter does) must not be undone on the next tick.
    // Keyed by group so the same building can still be resolved after a switch
    // away and back.
    const attempt = `${selectedGroupId}:${candidate}`
    if (!candidate || resolving.current === attempt) return
    resolving.current = attempt
    getPropertyDetail<IEntity>({ eId: candidate })
      .then((detail) => {
        // The token can name a building that has since been archived or moved
        // to another group; leave the session unselected rather than open it
        // somewhere the user no longer belongs.
        if (!detail || detail.Status === 'Archived') return
        if (detail.GroupId && selectedGroupId && detail.GroupId !== selectedGroupId) return
        // An empty selectedGroupId means the session never picked up a group;
        // repair it from the building's own record instead of leaving both
        // unselected forever.
        if (detail.GroupId && !selectedGroupId) {
          dispatch(selectedGroup({ groupId: detail.GroupId, groupName: '', group: {} }))
        }
        dispatch(
          selectedProperty({
            propertyId: candidate,
            propertyName: detail.Name?.en || '',
            buildingCode: detail.Code,
            propertyTag: detail.PropertyTag || {},
          })
        )
      })
      .catch(() => {
        /* no building to open on; the picker still works */
      })
  }, [user, selectedGroupId, selectedPropertyId, pathname, dispatch])
}
